<template>
  <MainLayout>
    <v-layout style="padding: 1em" justify-center row fill-height>
      <v-flex>
        <v-card style="height: 100%" light color="#f4f4fa">
          <v-layout column style="height: 100%">
            <!-- Title -->
            <v-card-title row>
              <span class="title font-weight-light">District Admin</span>
            </v-card-title>

            <!-- Searching, Actions -->
            <div class="d-flex justify-end align-center px-4">
              <v-text-field
                label="Search by name, email, phone"
                class="flex-none mr-2"
                style="width: 40%"
                v-model="filters.q"
                @input="getAdmins"
                prepend-icon="search"
                clearable
              />

              <v-space />

              <v-btn
                @click="
                  editDialogVisible = true;
                  resetForm();
                "
                class="flex-none"
                color="primary"
              >
                <v-icon>add</v-icon>
                Create Admin
              </v-btn>
            </div>

            <!-- Table -->
            <v-container fluid fill-height class="full-height">
              <v-layout style="padding: 0" class="full-height">
                <v-flex sm12 md12 class="full-height scroll">
                  <v-data-table
                    :headers="headers"
                    :items="admins"
                    :pagination.sync="pagination"
                    :rows-per-page-items="[10, 50, 100]"
                    :total-items="pagination.totalItems"
                    :loading="loading"
                    @update:pagination="getAdmins"
                    class="elevation-1"
                  >
                    <!-- When items = [] -->
                    <template v-slot:no-data>
                      <div style="text-align: center">
                        {{
                          loading
                            ? "Please wait a moment "
                            : "No matching records found"
                        }}
                      </div>
                    </template>

                    <!-- When items = undefined -->
                    <template v-slot:no-results>
                      <div style="text-align: center">
                        {{
                          loading
                            ? "Please wait a moment "
                            : "Data will be displayed in here"
                        }}
                      </div>
                    </template>

                    <template v-slot:items="props">
                      <td>{{ props.item.id }}</td>
                      <td>{{ props.item.name }}</td>
                      <td>{{ props.item.email }}</td>
                      <td>{{ props.item.mobile }}</td>
                      <td>
                        {{ props.item.district && props.item.district.name }}
                      </td>

                      <!-- Actions -->
                      <td>
                        <v-btn @click="editItem(props.item)" icon title="Edit">
                          <v-icon color="warning">edit</v-icon>
                        </v-btn>

                        <v-btn
                          @click="confirmDelete(props.item)"
                          icon
                          title="Delete"
                        >
                          <v-icon color="error">delete</v-icon>
                        </v-btn>
                      </td>
                    </template>
                  </v-data-table>
                </v-flex>
              </v-layout>
            </v-container>
          </v-layout>
        </v-card>
      </v-flex>
    </v-layout>

    <!-- Add/Edit Admin Dialog -->
    <v-dialog v-model="editDialogVisible" width="37.5em">
      <v-flex light sm12 md12 class="full-height scroll">
        <!-- Header -->
        <v-toolbar color="primary" dark>
          <v-toolbar-title>
            {{ form.id ? "Edit Admin" : "Create New Admin" }}</v-toolbar-title
          >
          <v-spacer />
          <v-btn icon @click="editDialogVisible = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <!-- Content -->
        <v-card light>
          <v-container fluid grid-list-lg>
            <v-layout column>
              <!-- District -->
              <div class="form-item-view">
                <strong class="form-item-label">
                  District
                  <span style="color: var(--danger)">*</span>
                </strong>

                <v-select
                  v-model="form.district_id"
                  :items="districts"
                  item-value="id"
                  item-text="name"
                  clearable
                  required
                />
              </div>

              <!-- Contact Person Info -->
              <div class="form-item-view">
                <strong class="form-item-label">
                  Full Name
                  <span style="color: var(--danger)">*</span>
                </strong>

                <v-text-field
                  v-model="form.name"
                  :error-messages="errors.name"
                  placeholder="Enter Name"
                />
              </div>

              <div class="form-item-view">
                <strong class="form-item-label">
                  Email
                  <span style="color: var(--danger)">*</span>
                </strong>

                <v-text-field
                  :rules="[rules.email]"
                  v-model="form.email"
                  placeholder="Enter Email"
                  :error-messages="errors.email"
                />
              </div>

              <div class="form-item-view">
                <strong class="form-item-label">
                  Phone No.
                  <span style="color: var(--danger)">*</span>
                </strong>

                <v-text-field
                  v-model="form.mobile"
                  mask="##### #####"
                  prefix="+91"
                  :error-messages="errors.mobile"
                  placeholder="Enter Phone"
                />
              </div>

              <!-- Password -->
              <div class="form-item-view">
                <strong class="form-item-label">Password</strong>
                <v-text-field
                  browser-autocomplete="new-password"
                  :type="'password'"
                  :error-messages="errors.password"
                  v-model="form.password"
                  placeholder="Password"
                />
              </div>

              <div class="form-item-view">
                <strong class="form-item-label">
                  Confirm Password
                </strong>

                <v-text-field
                  browser-autocomplete="new-password"
                  :type="'password'"
                  placeholder="Re-type Password"
                  :error-messages="errors.password_confirmation"
                  v-model="form.password_confirmation"
                />
              </div>

              <div
                class="form-item-view"
                style="display: flex; width: 100%; justify-content: flex-end"
              >
                <v-btn flat @click="editDialogVisible = false">Cancel</v-btn>

                <v-btn
                  v-if="onEdit"
                  :loading="updateLoading"
                  color="primary"
                  @click="updateInformation"
                >
                  Update
                </v-btn>

                <v-btn
                  v-if="!onEdit"
                  :loading="updateLoading"
                  color="primary"
                  @click="submitForm"
                >
                  Create
                </v-btn>
              </div>
            </v-layout>
          </v-container>
        </v-card>
      </v-flex>
    </v-dialog>
  </MainLayout>
</template>

<script>
import { debounce } from "@/utils/Debounce";

import moment from "moment";
import sdk from "@/api/sdk";

export default {
  metaInfo: {
    title: "District Admins",
    meta: [{ name: "District Admins", content: "District admins management" }]
  },

  data() {
    return {
      // List
      loading: false,
      admins: undefined,
      headers: [
        {
          text: "ID",
          width: 120,
          value: "id",
          sortable: false
        },
        {
          text: "Name",
          value: "name",
          sortable: true
        },
        {
          text: "Email",
          value: "email",
          sortable: true
        },
        {
          text: "Phone",
          value: "mobile",
          width: 100,
          sortable: false
        },
        {
          text: "District",
          value: "district",
          sortable: false
        },
        {
          text: "Actions",
          value: "action",
          width: 200,
          sortable: false
        }
      ],

      filters: {
        q: undefined
      },

      pagination: {
        descending: false,
        page: 1,
        rowsPerPage: 10,
        sortBy: null,
        totalItems: 0
      },

      // Users
      fetchingUsers: false,
      users: [],
      districts: [],

      // Form
      editDialogVisible: false,
      onEdit: false,
      updateLoading: false,

      form: {},
      errors: {},
      rules: {
        required: value => !!value || "Required.",
        counter: value => (value && value.length <= 10) || "Max 10 characters",
        email: value => {
          if (!value) return true;

          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return pattern.test(value) || "Invalid e-mail.";
        }
      }
    };
  },

  computed: {
    defaultAvatar() {
      return require("@/assets/images/avatar.png");
    }
  },

  watch: {
    editDialogVisible(val) {
      if (!val) {
        this.clearDialogForm();
      }
    }
  },

  created() {
    this.getDistricts();
    this.getAdmins();
  },

  methods: {
    clearDialogForm() {
      this.form = {};
      this.errors = {};
    },

    convertTime(utcStr) {
      return moment
        .parseZone(utcStr, "YYYY-MM-DD HH:mm:ss", "UTC")
        .toDate()
        .toLocaleTimeString();
    },

    convertDate(utcStr) {
      return moment
        .parseZone(utcStr, "YYYY-MM-DD HH:mm:ss", "UTC")
        .toDate()
        .toLocaleDateString();
    },

    resetForm() {
      this.onEdit = false;
      this.form = {
        id: undefined,
        name: undefined,
        password: undefined,
        password_confirmation: undefined,
        mobile: undefined,
        email: undefined,
        district_id: undefined
      };
    },

    async getDistricts() {
      const res = await sdk.request({
        url: "/district/all"
      });

      this.districts = res.data.data;
    },

    async submitForm() {
      try {
        this.errors = {};
        this.updateLoading = true;
        await sdk.request({
          method: "POST",
          url: "/district/users",
          data: this.form
        });

        this.updateLoading = false;
        this.$notify.success("The admin has been created");
        this.resetForm();
        this.getAdmins();

        this.editDialogVisible = false;
      } catch (e) {
        console.error(e);

        if (e.errors) {
          this.errors = e.errors;
        } else {
          this.$notify.error(e.message);
        }
      } finally {
        this.updateLoading = false;
      }
    },

    async updateInformation() {
      try {
        this.errors = {};
        this.updateLoading = true;

        await sdk.request({
          method: "PUT",
          url: `/district/users/${this.form.id}`,
          data: this.form
        });

        this.$notify.success("The admin information has been updated");
        this.getAdmins();

        this.editDialogVisible = false;
      } catch (e) {
        console.error(e);

        if (e.errors) {
          this.errors = e.errors;
        } else {
          this.$notify.error(e.message);
        }
      } finally {
        this.updateLoading = false;
      }
    },

    getAdmins: debounce(async function() {
      try {
        this.loading = true;
        this.admins = undefined;

        const res = await sdk.request({
          url: "/district/users",
          params: {
            q: this.filters.q || undefined,
            page_size: this.pagination.rowsPerPage,
            page: this.pagination.page
          }
        });

        this.admins = res.data.data;

        this.pagination.totalItems = res.data.total;
      } finally {
        this.loading = false;
      }
    }, 500),

    editItem(admin) {
      this.editDialogVisible = true;
      this.onEdit = true;

      this.form = {
        id: admin.id,
        name: admin.name,
        password: undefined,
        password_confirmation: undefined,
        mobile: admin.mobile,
        email: admin.email,
        district_id: admin.district && admin.district.id
      };
    },

    async confirmDelete(admin) {
      try {
        await this.$confirm(
          "Are you sure delete admin, all relation data of admin will lose?",
          "Warning",
          {
            confirmButtonText: "OK",
            cancelButtonText: "Cancel",
            type: "warning"
          }
        );

        this.deleteAdmin(admin);
      } catch (error) {
        // Ignore
      }
    },

    async deleteAdmin(admin) {
      try {
        this.loading = true;

        await sdk.request({
          method: "DELETE",
          url: `/district/users/${admin.id}`
        });

        this.$notify.success("Admin has been successfully deleted.");
        this.getAdmins();
      } catch (error) {
        this.$notify.error(error.message);
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style lang="css" scoped>
.form-item-view {
  display: flex;
  width: 100%;
  align-items: center;
}
.form-item-label {
  width: 10em;
  text-align: left;
  margin-right: 1.25em;
  /* margin-bottom: 1.25em; */
}
.header-contact {
  font-size: 1.25em;
  margin-bottom: 0.625em;
}
.full-height {
  flex: 1 1 auto;
  min-height: 0;
}
.scroll {
  overflow-y: auto;
}
</style>
